/**
 * Outil « Mouvement et énergie » (Boîte à outils, physique-chimie 6e → Tle).
 *
 * Explorer : une scène (voiture à vitesse constante, qui démarre, qui freine ; nacelle de manège ;
 * balle lâchée ou lancée), un film qu'on lance ou qu'on parcourt au curseur, la
 * chronophotographie (positions à intervalles de temps égaux), la trajectoire. Selon le niveau :
 * référentiel de la voiture (collège, 2nde), vecteurs vitesse (2nde), Δv⃗ (1re), accélération
 * (Tle), énergies cinétique, de position et mécanique (3e, 1re, Tle), frottements de l'air.
 * Les phrases du prof sous la figure disent ce qu'on voit et la loi qui s'applique. Défi : cinq
 * questions selon le niveau. Logique : `motion.ts`.
 */

import { useEffect, useMemo, useRef, useState, type SVGProps } from 'react';
import { Pause, Play } from 'lucide-react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { Note, ParamSlider, Segmented, Stepper, ToggleChip } from '../../ui/controls';
import { frenchSpaces } from '../../ui/format';
import { McqChallenge } from '../../ui/mcq-challenge';
import { RichText } from '../../ui/rich-text';
import { FIGURE_BG_CLASS, HaloText, VectorArrow } from '../../ui/svg-parts';
import {
  BRAKE_DRY,
  BRAKE_WET,
  MERRY_R,
  ROAD_L,
  SCENES,
  VUNIT,
  AUNIT,
  accelerations,
  chrono,
  chronoTau,
  deltaVs,
  energies,
  fr,
  joules,
  kmh,
  lawLines,
  makeQuiz,
  meters,
  ms,
  quizKinds,
  sceneById,
  seconds,
  simulate,
  speed,
  stateAt,
  sub,
  velocities,
  type MotionInput,
  type Point,
  type SceneId,
  type Sim,
  type Vec,
} from './motion';

const W = 340;
const H = 230;
const PAD = 24;
const INK = 'var(--color-ink-700)';
const MUTED = 'var(--color-ink-400)';
const OBJ = 'var(--color-primary-500)';
const VCOL = 'var(--color-primary-500)';
const DVCOL = 'var(--color-accent-600)';
const ACOL = 'var(--color-error-500)';
const TREE = 'var(--color-success-600)';

type VecMode = 'none' | 'v' | 'dv' | 'a';

interface View {
  /** monde (m) → écran (px) */
  X: (x: number) => number;
  Y: (z: number) => number;
  /** px par mètre */
  s: number;
  /** hauteur de la figure (px) */
  h: number;
}

/** voitures : arbres, bande de film (chronophotographie), route, repères de distance */
const CAR = { crown: 46, film: 84, road: 96, lane: 108, ticks: 134, brace: 146 } as const;

/** cadrage de la scène */
function viewOf(input: MotionInput, sim: Sim, carFrame: boolean): View {
  const kind = sceneById(input.scene).kind;
  if (kind === 'car') {
    const L =
      input.scene === 'brake' ? Math.max(20, Math.ceil((sim.dR + sim.dF) / 10) * 10) : ROAD_L;
    const s = (W - 2 * PAD) / L;
    const x0 = carFrame ? -L : 0;
    return {
      s,
      h: input.scene === 'brake' && !carFrame ? 170 : 146,
      X: (x) => PAD + (x - x0) * s,
      Y: () => CAR.film,
    };
  }
  if (kind === 'merry') {
    const s = (H - 50) / (2 * MERRY_R + 2);
    return { s, h: H, X: (x) => W / 2 + x * s, Y: (z) => H / 2 - z * s };
  }
  let xmax = 0;
  let zmax = input.h;
  for (const st of sim.states) {
    xmax = Math.max(xmax, st.x);
    zmax = Math.max(zmax, st.z);
  }
  const wx = Math.max(xmax, 4);
  const s = Math.min((W - 2 * PAD - 30) / wx, (H - 52) / Math.max(zmax, 1));
  const x0 = input.scene === 'drop' ? -(W / 2 - PAD - 30) / s : 0;
  return { s, h: H, X: (x) => PAD + 30 + (x - x0) * s, Y: (z) => H - 26 - z * s };
}

/** valeur ronde pour la légende d'échelle */
function niceRef(v: number): number {
  const p = 10 ** Math.floor(Math.log10(v));
  return (
    [1, 2, 5, 10]
      .map((k) => k * p)
      .filter((r) => r <= v)
      .pop() ?? p
  );
}

/** texte SVG avec indices (« d_R ») */
function SvgSub({ text, ...props }: { text: string } & SVGProps<SVGTextElement>) {
  const parts = text.split(/([A-Za-z])_([A-Za-z]+)/);
  return (
    <HaloText {...props}>
      {parts.map((p, i) =>
        i % 3 === 2 ? (
          <tspan key={i} baselineShift="sub" fontSize="0.75em">
            {p}
          </tspan>
        ) : (
          <tspan key={i}>{p}</tspan>
        ),
      )}
    </HaloText>
  );
}

function Figure({
  input,
  sim,
  t,
  rank,
  carFrame,
  showChrono,
  showPath,
  vecMode,
}: {
  input: MotionInput;
  sim: Sim;
  t: number;
  rank: number | null;
  carFrame: boolean;
  showChrono: boolean;
  showPath: boolean;
  vecMode: VecMode;
}) {
  const kind = sceneById(input.scene).kind;
  const view = viewOf(input, sim, carFrame);
  const { X, Y } = view;
  const HH = view.h;
  const tau = chronoTau(sim.T);
  const pts = chrono(sim, tau);
  const now = stateAt(sim, t);
  const centered = rank === 7;

  const vecs: Vec[] =
    vecMode === 'v'
      ? velocities(pts, tau, centered)
      : vecMode === 'dv'
        ? deltaVs(pts, tau)
        : vecMode === 'a'
          ? accelerations(pts, tau)
          : [];
  const vmax = Math.max(1e-9, ...vecs.map((v) => Math.hypot(v.x, v.z)));
  const k = 38 / vmax;
  const vColor = vecMode === 'v' ? VCOL : vecMode === 'dv' ? DVCOL : ACOL;
  const unit = vecMode === 'a' ? AUNIT(rank) : VUNIT(rank);
  const ref = niceRef(vmax);

  // étiquettes M₀, M₁… : pas deux à moins de 15 px ; manège : vers l'extérieur du cercle
  const labels: Array<{ p: Point; x: number; y: number }> = [];
  for (const p of pts) {
    let x = X(p.x);
    let y = Y(p.z);
    if (kind === 'car') y -= 9;
    else if (kind === 'merry') {
      const r = Math.hypot(p.x, p.z) || 1;
      x += (p.x / r) * 13;
      y -= (p.z / r) * 13;
    } else x += 9;
    const prev = labels[labels.length - 1];
    if (prev && Math.hypot(prev.x - x, prev.y - y) < 15) continue;
    labels.push({ p, x, y });
  }

  const pathD = sim.states
    .filter((_, i) => i % 4 === 0 || i === sim.states.length - 1)
    .map((st, i) => `${i === 0 ? 'M' : 'L'} ${X(st.x).toFixed(1)} ${Y(st.z).toFixed(1)}`)
    .join(' ');

  return (
    <svg
      viewBox={`0 0 ${W} ${HH}`}
      className={`${FIGURE_BG_CLASS} w-full rounded-2xl`}
      role="img"
      aria-label={`Scène ${sceneById(input.scene).label}, instant ${seconds(t)}`}
      data-testid="mv-figure"
    >
      {/* décor */}
      {kind === 'car' && (
        <g>
          <rect x={0} y={CAR.road} width={W} height={24} fill="var(--color-ink-200)" />
          <line
            x1={0}
            y1={CAR.lane}
            x2={W}
            y2={CAR.lane}
            stroke="var(--color-ink-50)"
            strokeWidth={2}
            strokeDasharray="10 8"
          />
          {Array.from({ length: 40 }, (_, j) => {
            const xw = (j - 10) * 20;
            const xs = X(carFrame ? xw - now.x : xw);
            if (xs < -10 || xs > W + 10 || (!carFrame && xw < 0)) return null;
            return (
              <g key={j}>
                <line
                  x1={xs}
                  y1={CAR.crown + 4}
                  x2={xs}
                  y2={CAR.crown + 18}
                  stroke="var(--color-ink-500)"
                  strokeWidth={2}
                />
                <circle cx={xs} cy={CAR.crown} r={7} fill={TREE} opacity={0.8} />
                {!carFrame && (
                  <text x={xs} y={CAR.ticks} textAnchor="middle" fontSize={10} fill={MUTED}>
                    {`${fr(xw, 0)} m`}
                  </text>
                )}
              </g>
            );
          })}
          {input.scene === 'brake' && !carFrame && (
            <g fontSize={10} fill={INK}>
              <line
                x1={X(sim.dR)}
                y1={CAR.road}
                x2={X(sim.dR)}
                y2={CAR.brace}
                stroke={INK}
                strokeDasharray="3 3"
              />
              <line
                x1={X(0)}
                y1={CAR.brace}
                x2={X(sim.dR)}
                y2={CAR.brace}
                stroke={INK}
                strokeWidth={2}
              />
              <line
                x1={X(sim.dR)}
                y1={CAR.brace}
                x2={X(sim.dR + sim.dF)}
                y2={CAR.brace}
                stroke="var(--color-error-500)"
                strokeWidth={2}
              />
              <SvgSub
                text={`d_R ${meters(sim.dR)}`}
                x={(X(0) + X(sim.dR)) / 2}
                y={CAR.brace + 14}
                textAnchor="middle"
              />
              <SvgSub
                text={`d_F ${meters(sim.dF)}`}
                x={(X(sim.dR) + X(sim.dR + sim.dF)) / 2}
                y={CAR.brace + 14}
                textAnchor="middle"
              />
            </g>
          )}
        </g>
      )}
      {kind === 'merry' && (
        <g>
          <circle
            cx={X(0)}
            cy={Y(0)}
            r={MERRY_R * view.s}
            fill="none"
            stroke="var(--color-ink-300)"
            strokeWidth={1.5}
          />
          <circle cx={X(0)} cy={Y(0)} r={3} fill={INK} />
          <HaloText x={X(0) + 6} y={Y(0) - 6} fontSize={11} fill={INK}>
            O
          </HaloText>
          <text x={PAD} y={18} fontSize={11} fill={MUTED}>
            vue de dessus
          </text>
        </g>
      )}
      {kind === 'ball' && (
        <g>
          <line x1={0} y1={Y(0)} x2={W} y2={Y(0)} stroke="var(--color-ink-500)" strokeWidth={2} />
          {Array.from({ length: 6 }, (_, j) => {
            const zmax = (H - 52) / view.s;
            const step = niceRef(zmax / 4);
            const z = j * step;
            if (z > zmax || j === 0) return null;
            return (
              <g key={j}>
                <line x1={PAD} y1={Y(z)} x2={PAD + 6} y2={Y(z)} stroke={MUTED} />
                <text x={PAD + 9} y={Y(z) + 3} fontSize={9} fill={MUTED}>{`${fr(z, 1)} m`}</text>
              </g>
            );
          })}
        </g>
      )}

      {/* trajectoire */}
      {showPath && !carFrame && kind !== 'car' && (
        <path
          d={pathD}
          fill="none"
          stroke="var(--color-ink-300)"
          strokeWidth={1.5}
          strokeDasharray="4 4"
        />
      )}
      {showPath && !carFrame && kind === 'car' && (
        <line
          x1={X(0)}
          y1={CAR.film}
          x2={X(pts.at(-1)!.x)}
          y2={CAR.film}
          stroke="var(--color-ink-300)"
          strokeWidth={1.5}
          strokeDasharray="4 4"
        />
      )}

      {/* chronophotographie */}
      {showChrono && (
        <g data-testid="mv-chrono">
          {carFrame
            ? pts.map((p) => (
                // positions de l'arbre planté en x = 0, vues depuis la voiture
                <circle
                  key={p.i}
                  cx={X(-stateAt(sim, p.t).x)}
                  cy={CAR.film}
                  r={3.2}
                  fill={TREE}
                  opacity={p.t <= t + 1e-9 ? 1 : 0.3}
                />
              ))
            : pts.map((p) => (
                <circle
                  key={p.i}
                  cx={X(p.x)}
                  cy={Y(p.z)}
                  r={3.2}
                  fill={INK}
                  opacity={p.t <= t + 1e-9 ? 1 : 0.3}
                />
              ))}
          {!carFrame &&
            labels.map(({ p, x, y }) => (
              <HaloText
                key={p.i}
                x={x}
                y={y + 3}
                textAnchor={kind === 'ball' ? 'start' : 'middle'}
                fontSize={9}
                fill={INK}
                opacity={p.t <= t + 1e-9 ? 1 : 0.45}
              >
                {`M${sub(p.i)}`}
              </HaloText>
            ))}
        </g>
      )}

      {/* vecteurs */}
      {!carFrame &&
        vecs.map((v, i) => (
          <VectorArrow
            key={i}
            x1={X(v.at.x)}
            y1={Y(v.at.z)}
            x2={X(v.at.x) + v.x * k}
            y2={Y(v.at.z) - v.z * k}
            color={vColor}
            width={2.2}
          />
        ))}
      {vecs.length > 0 && !carFrame && (
        <g data-testid="mv-scale">
          <VectorArrow
            x1={W - PAD - ref * k}
            y1={12}
            x2={W - PAD}
            y2={12}
            color={vColor}
            width={2.2}
          />
          <HaloText x={W - PAD} y={27} textAnchor="end" fontSize={10} fill={INK}>
            {`${vecMode === 'dv' ? 'Δv' : vecMode === 'a' ? 'a' : 'v'} : ${fr(ref, 2)} ${unit}`}
          </HaloText>
        </g>
      )}

      {/* l'objet à l'instant t */}
      {kind === 'car' ? (
        <g data-testid="mv-object" transform={`translate(${X(carFrame ? 0 : now.x)} ${CAR.lane})`}>
          <rect x={-14} y={-12} width={22} height={10} rx={3} fill={OBJ} />
          <rect x={-9} y={-18} width={12} height={7} rx={2} fill={OBJ} opacity={0.8} />
          <circle cx={-9} cy={-1} r={3} fill={INK} />
          <circle cx={3} cy={-1} r={3} fill={INK} />
        </g>
      ) : (
        <circle
          data-testid="mv-object"
          cx={X(now.x)}
          cy={Y(now.z)}
          r={kind === 'merry' ? 7 : 6}
          fill={OBJ}
        />
      )}
      {kind === 'car' && !carFrame && (
        <line
          x1={X(now.x) - 3}
          y1={CAR.film + 5}
          x2={X(now.x) - 3}
          y2={CAR.lane - 19}
          stroke={OBJ}
          strokeWidth={1.5}
          strokeDasharray="2 2"
        />
      )}
    </svg>
  );
}

function Readings({
  input,
  sim,
  t,
  rank,
  energy,
}: {
  input: MotionInput;
  sim: Sim;
  t: number;
  rank: number | null;
  energy: boolean;
}) {
  const kind = sceneById(input.scene).kind;
  const s = stateAt(sim, t);
  const v = speed(s);
  const e = energies(input, sim, t);
  const P = rank !== null && rank >= 6 ? 'E_pp' : 'E_p';
  const rows: Array<[string, string]> = [['Instant t', seconds(t)]];
  if (kind === 'car') rows.push(['Distance parcourue', meters(s.x)]);
  if (kind === 'ball')
    rows.push([rank !== null && rank >= 6 ? 'Altitude z' : 'Hauteur', meters(Math.max(s.z, 0))]);
  rows.push(['Vitesse', kind === 'car' ? `${ms(v, rank)} (${kmh(v)})` : ms(v, rank)]);
  if (energy) {
    rows.push(['Énergie cinétique E_c', joules(e.Ec)]);
    if (kind === 'ball') {
      rows.push([`Énergie ${P === 'E_pp' ? 'potentielle' : 'de position'} ${P}`, joules(e.Ep)]);
      rows.push(['Énergie mécanique E_m', joules(e.Em)]);
    }
  }
  return (
    <table className="w-full text-sm" data-testid="mv-readings">
      <tbody>
        {rows.map(([k, val]) => (
          <tr key={k} className="border-ink-100 border-b last:border-0">
            <th scope="row" className="text-ink-600 py-1.5 pr-2 text-left font-normal">
              <RichText>{k}</RichText>
            </th>
            <td className="text-ink-900 py-1.5 text-right font-semibold tabular-nums">
              {frenchSpaces(val)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function EnergyBars({
  input,
  sim,
  t,
  rank,
}: {
  input: MotionInput;
  sim: Sim;
  t: number;
  rank: number | null;
}) {
  const kind = sceneById(input.scene).kind;
  const e = energies(input, sim, t);
  const e0 = energies(input, sim, 0);
  const eEnd = energies(input, sim, sim.T);
  const max = Math.max(e0.Em, eEnd.Em, 1e-9);
  const P = rank !== null && rank >= 6 ? 'E_pp' : 'E_p';
  const bars: Array<[string, number, string]> = [['E_c', e.Ec, OBJ]];
  if (kind === 'ball') bars.push([P, e.Ep, 'var(--color-accent-500)'], ['E_m', e.Em, INK]);
  if (e.lost > 0 || input.drag || input.scene === 'brake')
    bars.push(['Thermique', e.lost, 'var(--color-error-500)']);
  if (input.scene === 'start') bars.push(['Moteur', e.engine, 'var(--color-success-600)']);
  return (
    <div className="space-y-1.5" data-testid="mv-energy" aria-hidden>
      {bars.map(([name, val, color]) => (
        <div key={name} className="flex items-center gap-2 text-xs">
          <span className="text-ink-600 w-16 shrink-0">
            <RichText>{name}</RichText>
          </span>
          <span className="bg-ink-100 relative h-3 flex-1 overflow-hidden rounded-full">
            <span
              className="absolute inset-y-0 left-0 rounded-full"
              style={{ width: `${Math.min(100, (100 * val) / max)}%`, background: color }}
            />
          </span>
          <span className="text-ink-900 w-16 shrink-0 text-right font-semibold tabular-nums">
            {frenchSpaces(joules(val))}
          </span>
        </div>
      ))}
    </div>
  );
}

const CAR_MASSES = [800, 1000, 1200, 1500, 2000] as const;
const BALL_MASSES = [0.1, 0.2, 0.5, 1, 2] as const;

function Explore({ rank }: { rank: number | null }) {
  const all = rank === null;
  const energyRank = all || rank === 4 || (rank !== null && rank >= 6);
  const vectorRank = all || (rank !== null && rank >= 5);
  const frameRank = all || (rank !== null && rank <= 5);
  const dragRank = all || (rank !== null && rank >= 4);

  const [scene, setScene] = useState<SceneId>('road');
  const [kmhRoad, setKmhRoad] = useState(50);
  const [accel, setAccel] = useState(2);
  const [kmhBrake, setKmhBrake] = useState(50);
  const [wet, setWet] = useState(false);
  const [reaction, setReaction] = useState(1);
  const [vMerry, setVMerry] = useState(3);
  const [hDrop, setHDrop] = useState(10);
  const [v0, setV0] = useState(10);
  const [angle, setAngle] = useState(45);
  const [hThrow, setHThrow] = useState(1);
  const [carMass, setCarMass] = useState(2);
  const [ballMass, setBallMass] = useState(2);
  const [drag, setDrag] = useState(false);
  const [carFrame, setCarFrame] = useState(false);
  const [showChrono, setShowChrono] = useState(true);
  const [showPath, setShowPath] = useState(true);
  const [vecMode, setVecMode] = useState<VecMode>(
    rank === 6 ? 'dv' : rank === 7 ? 'a' : vectorRank ? 'v' : 'none',
  );
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(false);

  const kind = sceneById(scene).kind;
  const input: MotionInput = {
    scene,
    v0:
      scene === 'road'
        ? kmhRoad / 3.6
        : scene === 'brake'
          ? kmhBrake / 3.6
          : scene === 'merry'
            ? vMerry
            : v0,
    a: scene === 'brake' ? (wet ? BRAKE_WET : BRAKE_DRY) : accel,
    reaction,
    h: scene === 'drop' ? hDrop : hThrow,
    angle,
    m: kind === 'car' ? CAR_MASSES[carMass]! : kind === 'merry' ? 40 : BALL_MASSES[ballMass]!,
    drag: kind === 'ball' && drag,
  };
  const key = JSON.stringify(input);
  const sim = useMemo(() => simulate(input), [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const T = sim.T;

  // nouvelle scène ou nouveaux réglages : on revient au début
  useEffect(() => {
    setT(0);
    setPlaying(false);
  }, [key]);

  // lecture du film : 2,5 à 8 s à l'écran selon la durée réelle
  const raf = useRef<number | null>(null);
  useEffect(() => {
    if (!playing) return;
    const rate = T / Math.min(8, Math.max(2.5, T));
    let last = performance.now();
    const step = (now: number) => {
      const dt = ((now - last) / 1000) * rate;
      last = now;
      let stop = false;
      setT((x) => {
        const nx = Math.min(T, x + dt);
        if (nx >= T) stop = true;
        return nx;
      });
      if (stop) setPlaying(false);
      else raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => {
      if (raf.current !== null) cancelAnimationFrame(raf.current);
    };
  }, [playing, T]);

  const lines = lawLines({ input, sim, t, rank, carFrame: kind === 'car' && carFrame });
  const tau = chronoTau(T);
  const nImages = chrono(sim, tau).length;

  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Scène"
        value={scene}
        options={SCENES.map((s) => ({ value: s.id, label: s.label }))}
        onChange={(v) => {
          setScene(v);
          setCarFrame(false);
        }}
      />
      <Figure
        input={input}
        sim={sim}
        t={t}
        rank={rank}
        carFrame={kind === 'car' && carFrame}
        showChrono={showChrono}
        showPath={showPath}
        vecMode={vectorRank ? vecMode : 'none'}
      />
      <div className="flex items-center gap-3">
        <button
          type="button"
          aria-label={playing ? 'Pause' : 'Lancer le film'}
          aria-pressed={playing}
          onClick={() => {
            if (!playing && t >= T - 1e-9) setT(0);
            setPlaying(!playing);
          }}
          className="bg-primary-500 flex min-h-11 min-w-11 items-center justify-center rounded-xl text-white active:scale-[0.98]"
        >
          {playing ? (
            <Pause className="h-5 w-5" aria-hidden />
          ) : (
            <Play className="h-5 w-5" aria-hidden />
          )}
        </button>
        <div className="flex-1">
          <ParamSlider
            name="Instant t"
            ariaLabel="Instant"
            value={Math.round(t * 100) / 100}
            min={0}
            max={Math.round(T * 100) / 100}
            step={0.01}
            onChange={(v) => {
              setPlaying(false);
              setT(Math.min(v, T));
            }}
            format={(v) => frenchSpaces(seconds(v))}
          />
        </div>
      </div>
      <p className="text-ink-500 text-xs">
        {frenchSpaces(
          `Chronophotographie : une image toutes les ${fr(tau, 2)} s (${nImages} images).`,
        )}
      </p>
      <div className="flex flex-wrap gap-2">
        <ToggleChip on={showChrono} onClick={() => setShowChrono(!showChrono)}>
          Chronophotographie
        </ToggleChip>
        <ToggleChip on={showPath} onClick={() => setShowPath(!showPath)}>
          Trajectoire
        </ToggleChip>
        {kind === 'car' && frameRank && (
          <ToggleChip on={carFrame} onClick={() => setCarFrame(!carFrame)}>
            Référentiel de la voiture
          </ToggleChip>
        )}
        {kind === 'ball' && dragRank && (
          <ToggleChip on={drag} onClick={() => setDrag(!drag)}>
            Frottements de l’air
          </ToggleChip>
        )}
      </div>
      {vectorRank && !(kind === 'car' && carFrame) && (
        <Segmented
          ariaLabel="Vecteurs"
          value={vecMode}
          options={[
            { value: 'none', label: 'Aucun' },
            { value: 'v', label: 'Vitesse' },
            ...(all || (rank !== null && rank >= 6) ? [{ value: 'dv' as const, label: 'Δv' }] : []),
            ...(all || rank === 7 ? [{ value: 'a' as const, label: 'Accélération' }] : []),
          ]}
          onChange={setVecMode}
        />
      )}
      {scene === 'road' && (
        <ParamSlider
          name="Vitesse"
          ariaLabel="Vitesse"
          value={kmhRoad}
          min={20}
          max={130}
          step={10}
          onChange={setKmhRoad}
          format={(v) => `${v} km/h`}
        />
      )}
      {scene === 'start' && (
        <ParamSlider
          name="Accélération"
          ariaLabel="Accélération"
          value={accel}
          min={1}
          max={4}
          step={0.5}
          onChange={setAccel}
          format={(v) => frenchSpaces(`${fr(v, 1)} ${AUNIT(rank)}`)}
        />
      )}
      {scene === 'brake' && (
        <>
          <ParamSlider
            name="Vitesse au début"
            ariaLabel="Vitesse au début"
            value={kmhBrake}
            min={30}
            max={130}
            step={10}
            onChange={setKmhBrake}
            format={(v) => `${v} km/h`}
          />
          <ParamSlider
            name="Temps de réaction"
            ariaLabel="Temps de réaction"
            value={reaction}
            min={0.5}
            max={2}
            step={0.5}
            onChange={setReaction}
            format={(v) => frenchSpaces(seconds(v))}
          />
          <Segmented
            ariaLabel="Route"
            value={wet ? 'wet' : 'dry'}
            options={[
              { value: 'dry', label: 'Route sèche' },
              { value: 'wet', label: 'Route mouillée' },
            ]}
            onChange={(v) => setWet(v === 'wet')}
          />
        </>
      )}
      {scene === 'merry' && (
        <ParamSlider
          name="Vitesse"
          ariaLabel="Vitesse"
          value={vMerry}
          min={1}
          max={5}
          step={0.5}
          onChange={setVMerry}
          format={(v) => frenchSpaces(ms(v, rank))}
        />
      )}
      {scene === 'drop' && (
        <ParamSlider
          name="Hauteur de départ"
          ariaLabel="Hauteur de départ"
          value={hDrop}
          min={1}
          max={20}
          step={1}
          onChange={setHDrop}
          format={(v) => frenchSpaces(meters(v))}
        />
      )}
      {scene === 'throw' && (
        <>
          <ParamSlider
            name="Vitesse de lancer"
            ariaLabel="Vitesse de lancer"
            value={v0}
            min={4}
            max={20}
            step={1}
            onChange={setV0}
            format={(v) => frenchSpaces(ms(v, rank))}
          />
          <ParamSlider
            name="Angle α"
            ariaLabel="Angle de lancer"
            value={angle}
            min={10}
            max={80}
            step={5}
            onChange={setAngle}
            format={(v) => `${v}°`}
          />
          <ParamSlider
            name="Hauteur de départ"
            ariaLabel="Hauteur de départ"
            value={hThrow}
            min={0}
            max={2}
            step={0.5}
            onChange={setHThrow}
            format={(v) => frenchSpaces(meters(v))}
          />
        </>
      )}
      {energyRank && kind !== 'merry' && (
        <Stepper
          label="Masse"
          value={kind === 'car' ? carMass : ballMass}
          min={0}
          max={4}
          onChange={kind === 'car' ? setCarMass : setBallMass}
          format={(i) =>
            kind === 'car' ? `${fr(CAR_MASSES[i]!, 0)} kg` : `${fr(BALL_MASSES[i]!, 1)} kg`
          }
        />
      )}
      <Readings input={input} sim={sim} t={t} rank={rank} energy={energyRank} />
      {energyRank && !(kind === 'car' && carFrame) && (
        <EnergyBars input={input} sim={sim} t={t} rank={rank} />
      )}
      <Note>
        {lines.map((l) => (
          <span key={l} className="mb-1 block last:mb-0" data-testid="mv-law">
            <RichText>{l}</RichText>
          </span>
        ))}
      </Note>
    </div>
  );
}

export function MotionTool({
  level = null,
  seed,
}: {
  /** niveau : scènes, vecteurs, énergies et lois selon la classe (6e → Tle) */
  level?: string | null;
  /** graine du défi (tests) */
  seed?: number;
}) {
  const rank = schoolLevelRank(level);
  const [mode, setMode] = useState<'explore' | 'quiz'>('explore');
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Partie de l’outil"
        value={mode}
        options={[
          { value: 'explore', label: 'Explorer' },
          { value: 'quiz', label: 'Défi' },
        ]}
        onChange={setMode}
      />
      <div hidden={mode !== 'explore'}>
        <Explore rank={rank} />
      </div>
      <div hidden={mode !== 'quiz'}>
        <McqChallenge kinds={quizKinds(rank)} make={makeQuiz} seed={seed} testId="mv" rich />
      </div>
    </div>
  );
}
